import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";

interface AmendmentLanguageModalProps {
  amendmentLanguage: string | null;
  onClose: () => void;
}

export function AmendmentLanguageModal({
  amendmentLanguage,
  onClose,
}: AmendmentLanguageModalProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    if (amendmentLanguage === null) return;

    try {
      await navigator.clipboard.writeText(amendmentLanguage);
      setCopied(true);
    } catch (error: unknown) {
      console.error("Failed to copy amendment language", error);
      toast({
        title: "Failed to copy amendment language",
        description: "Clipboard access is unavailable.",
      });
    }
  };

  const handleClose = (): void => {
    setCopied(false);
    onClose();
  };

  // NOTE: We are temporarily hiding this. We may bring back this modal eventually
  // but want to keep it around for now
  if (amendmentLanguage) {
    return null;
  }

  return (
    <Dialog
      open={Boolean(amendmentLanguage)}
      onOpenChange={(open: boolean) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Amendment Language</DialogTitle>
        </DialogHeader>

        <Textarea
          readOnly
          value={amendmentLanguage ?? ""}
          className="min-h-[240px] resize-y whitespace-pre-wrap"
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Close
          </Button>
          <Button type="button" onClick={handleCopy}>
            {copied ? (
              <Check className="mr-2 h-4 w-4" />
            ) : (
              <Copy className="mr-2 h-4 w-4" />
            )}
            {copied ? "Copied" : "Copy to Clipboard"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
